import { useState, useMemo } from 'react';
import { useProfile, useAssets, useCashflows } from '../../hooks/useApi';
import { formatAmount } from '../../utils/numberFormat';
import { Currency } from '../../types/api.types';
import styles from './PensionPage.module.scss';

// ─── Social Security estimation (SSA bend-point formula 2024) ───────────────

function estimateSocialSecurity(avgAnnualEarningsUSD: number, yearsWorked: number, claimingAge: number): number {
    if (yearsWorked <= 0 || avgAnnualEarningsUSD <= 0) return 0;
    // AIME: cap at 35 years, prorate for fewer
    const effectiveYears = Math.min(yearsWorked, 35);
    const aime = (avgAnnualEarningsUSD * (effectiveYears / 35)) / 12;
    // 2024 bend points
    const pia =
        aime <= 1174
            ? aime * 0.9
            : aime <= 7078
              ? 1174 * 0.9 + (aime - 1174) * 0.32
              : 1174 * 0.9 + (7078 - 1174) * 0.32 + (aime - 7078) * 0.15;
    // FRA = 67 for born after 1960; adjust for early/late claiming
    const fraAge = 67;
    const monthsDiff = (claimingAge - fraAge) * 12;
    let adjustedPia: number;
    if (monthsDiff < 0) {
        // Early: -5/9% per month for first 36 months, then -5/12%
        const early = Math.abs(monthsDiff);
        const first36 = Math.min(early, 36);
        const beyond36 = Math.max(0, early - 36);
        adjustedPia = pia * (1 - first36 * (5 / 900) - beyond36 * (5 / 1200));
    } else {
        // Late: +8% per year past FRA
        adjustedPia = pia * (1 + monthsDiff * (8 / 1200));
    }
    return Math.round(Math.max(0, adjustedPia));
}

// ─── CPP estimation (2024 rules) ─────────────────────────────────────────────

const CPP_MAX_MONTHLY_2024 = 1364.6; // CAD at age 65, full 39-year history
const CPP_YMPE_2024 = 68500; // Year's Maximum Pensionable Earnings

function estimateCPP(avgAnnualEarningsCAD: number, yearsContributed: number, claimingAge: number): number {
    if (yearsContributed <= 0 || avgAnnualEarningsCAD <= 0) return 0;
    const earningsFactor = Math.min(1, avgAnnualEarningsCAD / CPP_YMPE_2024);
    const historyFactor = Math.min(1, yearsContributed / 39);
    const baseCpp = CPP_MAX_MONTHLY_2024 * earningsFactor * historyFactor;
    // Claiming adjustment: ±0.6% per month before/after 65
    const monthsDiff = (claimingAge - 65) * 12;
    const adjusted = baseCpp * (1 + monthsDiff * 0.006);
    return Math.round(Math.max(0, adjusted));
}

// ─── OAS estimation (2024 rules) ─────────────────────────────────────────────

const OAS_MAX_MONTHLY_2024 = 713.34; // CAD at 65, 40 years Canadian residency

function estimateOAS(yearsCanadianResidency: number, claimingAge: number): number {
    if (yearsCanadianResidency <= 0) return 0;
    const partial = Math.min(1, yearsCanadianResidency / 40);
    const base = OAS_MAX_MONTHLY_2024 * partial;
    // Deferral: +0.6% per month past 65, up to age 70
    const monthsDeferred = Math.max(0, Math.min(claimingAge - 65, 5) * 12);
    return Math.round(base * (1 + monthsDeferred * 0.006));
}

// ─── NPS annuity estimation ───────────────────────────────────────────────────

function estimateNPSAnnuity(currentBalance: number, yearsToRetirement: number, expectedReturnPct: number): number {
    if (currentBalance <= 0) return 0;
    const projectedCorpus = currentBalance * Math.pow(1 + expectedReturnPct / 100, yearsToRetirement);
    // 40% must buy annuity; annuity rate ~6%/year
    const annualAnnuity = projectedCorpus * 0.4 * 0.06;
    return Math.round(annualAnnuity / 12);
}

// ─── Component ───────────────────────────────────────────────────────────────

export function PensionPage() {
    const { profile, loading: profileLoading } = useProfile();
    const { assets } = useAssets();
    const { cashflows, addCashflow, deleteCashflow } = useCashflows();

    // SS inputs
    const [ssEarnings, setSsEarnings] = useState(80000);
    const [ssYears, setSsYears] = useState(15);
    const [ssClaiming, setSsClaiming] = useState(67);
    const [ssSaving, setSsSaving] = useState(false);

    // CPP inputs
    const [cppEarnings, setCppEarnings] = useState(70000);
    const [cppYears, setCppYears] = useState(10);
    const [cppClaiming, setCppClaiming] = useState(65);
    const [cppSaving, setCppSaving] = useState(false);

    // OAS inputs
    const [oasYears, setOasYears] = useState(10);
    const [oasClaiming, setOasClaiming] = useState(65);
    const [oasSaving, setOasSaving] = useState(false);

    // NPS inputs — balance read from assets; user can override expected return
    const [npsReturn, setNpsReturn] = useState(9);
    const [npsSaving, setNpsSaving] = useState(false);

    const [savedMessage, setSavedMessage] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const residencyCountries = useMemo(
        () => new Set((profile?.residencyHistory ?? []).map((r) => r.country)),
        [profile],
    );
    const hasUS = residencyCountries.has('US');
    const hasCAN = residencyCountries.has('CAN');

    const npsAsset = assets.find((a) => a.accountType === 'NPS');
    const currentAge = profile?.currentAge ?? 40;
    const retirementAge = profile?.targetRetirementAge ?? 60;
    const yearsToRetirement = Math.max(0, retirementAge - currentAge);
    const existingPensions = cashflows.filter((c) => c.category === 'PENSION');

    // ── Estimates ──
    const ssMonthly = estimateSocialSecurity(ssEarnings, ssYears, ssClaiming);
    const cppMonthly = estimateCPP(cppEarnings, cppYears, cppClaiming);
    const oasMonthly = estimateOAS(oasYears, oasClaiming);
    const npsMonthly = npsAsset ? estimateNPSAnnuity(npsAsset.balance, yearsToRetirement, npsReturn) : 0;

    const fmt = (amt: number, cur: string) => formatAmount(Math.round(amt), cur as Currency);

    const showSuccess = (label: string) => {
        setSavedMessage(`${label} added to your projection.`);
        setTimeout(() => setSavedMessage(null), 4000);
    };

    const saveSS = async () => {
        setSsSaving(true);
        try {
            await addCashflow({
                type: 'INCOME',
                category: 'PENSION',
                country: 'US',
                amount: ssMonthly,
                currency: 'USD',
                growthRate: 2.0,
                startAge: ssClaiming,
                endAge: 95,
            });
            showSuccess('Social Security');
        } finally {
            setSsSaving(false);
        }
    };

    const saveCPP = async () => {
        setCppSaving(true);
        try {
            await addCashflow({
                type: 'INCOME',
                category: 'PENSION',
                country: 'CAN',
                amount: cppMonthly,
                currency: 'CAD',
                growthRate: 2.0,
                startAge: cppClaiming,
                endAge: 95,
            });
            showSuccess('CPP');
        } finally {
            setCppSaving(false);
        }
    };

    const saveOAS = async () => {
        setOasSaving(true);
        try {
            await addCashflow({
                type: 'INCOME',
                category: 'PENSION',
                country: 'CAN',
                amount: oasMonthly,
                currency: 'CAD',
                growthRate: 2.0,
                startAge: oasClaiming,
                endAge: 95,
            });
            showSuccess('OAS');
        } finally {
            setOasSaving(false);
        }
    };

    const saveNPS = async () => {
        if (!npsAsset) return;
        setNpsSaving(true);
        try {
            await addCashflow({
                type: 'INCOME',
                category: 'PENSION',
                country: 'INDIA',
                amount: npsMonthly,
                currency: 'INR',
                growthRate: 3.0,
                startAge: retirementAge,
                endAge: 95,
            });
            showSuccess('NPS annuity');
        } finally {
            setNpsSaving(false);
        }
    };

    if (profileLoading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading...</p>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <h1 className={styles.title}>Government Pensions</h1>
                <p className={styles.subtitle}>
                    Estimate your entitlements from Social Security, CPP/OAS, and NPS based on your work history. Saved
                    pensions flow directly into your retirement projection.
                </p>
            </div>

            {savedMessage && <div className={styles.successBanner}>{savedMessage}</div>}

            {existingPensions.length > 0 && (
                <div className={styles.existingSection}>
                    <h2 className={styles.sectionTitle}>Already in Projection</h2>
                    <div className={styles.existingList}>
                        {existingPensions.map((cf) => (
                            <div key={cf.cashFlowId} className={styles.existingItem}>
                                <span className={styles.existingLabel}>
                                    {cf.country === 'US'
                                        ? '🇺🇸 Social Security / US Pension'
                                        : cf.country === 'CAN'
                                          ? '🇨🇦 CPP / OAS / Canada Pension'
                                          : '🇮🇳 NPS / India Pension'}
                                </span>
                                <span className={styles.existingAmount}>
                                    {fmt(cf.amount, cf.currency)}/mo · starts age {cf.startAge}
                                </span>
                                <button
                                    className={styles.deleteButton}
                                    disabled={deletingId === cf.cashFlowId}
                                    onClick={async () => {
                                        setDeletingId(cf.cashFlowId);
                                        try {
                                            await deleteCashflow(cf.cashFlowId);
                                        } finally {
                                            setDeletingId(null);
                                        }
                                    }}
                                >
                                    {deletingId === cf.cashFlowId ? '…' : '✕'}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className={styles.cardsGrid}>
                {/* ── Social Security ── */}
                {hasUS && (
                    <div className={styles.pensionCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <span className={styles.cardFlag}>🇺🇸</span>
                                <h2 className={styles.cardTitle}>Social Security</h2>
                            </div>
                            <span className={styles.cardBadge}>US</span>
                        </div>
                        <p className={styles.cardDesc}>
                            Estimated using the SSA bend-point formula (2024). Based on your highest 35 years of indexed
                            earnings, prorated by years worked.
                        </p>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Average annual US earnings (USD)
                                <span className={styles.inputValue}>${ssEarnings.toLocaleString()}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={10000}
                                max={200000}
                                step={5000}
                                value={ssEarnings}
                                onChange={(e) => setSsEarnings(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>$10k</span>
                                <span>$200k</span>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Years of US work history
                                <span className={styles.inputValue}>{ssYears} yrs</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={1}
                                max={35}
                                value={ssYears}
                                onChange={(e) => setSsYears(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>1</span>
                                <span>35</span>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Claiming age
                                <span className={styles.inputValue}>{ssClaiming}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={62}
                                max={70}
                                value={ssClaiming}
                                onChange={(e) => setSsClaiming(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>62 (early −30%)</span>
                                <span>70 (+24%)</span>
                            </div>
                        </div>

                        <div className={styles.estimate}>
                            <span className={styles.estimateLabel}>Estimated monthly benefit</span>
                            <span className={styles.estimateAmount}>${ssMonthly.toLocaleString()}</span>
                            <span className={styles.estimateSubtext}>
                                ${(ssMonthly * 12).toLocaleString()}/year · starts age {ssClaiming}
                            </span>
                        </div>

                        <button className={styles.addButton} onClick={saveSS} disabled={ssSaving || ssMonthly === 0}>
                            {ssSaving ? 'Saving…' : 'Add to Projection'}
                        </button>
                    </div>
                )}

                {/* ── CPP ── */}
                {hasCAN && (
                    <div className={styles.pensionCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <span className={styles.cardFlag}>🇨🇦</span>
                                <h2 className={styles.cardTitle}>Canada Pension Plan (CPP)</h2>
                            </div>
                            <span className={styles.cardBadge}>CAN</span>
                        </div>
                        <p className={styles.cardDesc}>
                            Based on 2024 maximum CPP of C$1,364/month at age 65. Prorated by years contributed and
                            earnings relative to the YMPE (C$68,500).
                        </p>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Average annual Canadian earnings (CAD)
                                <span className={styles.inputValue}>C${cppEarnings.toLocaleString()}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={10000}
                                max={150000}
                                step={5000}
                                value={cppEarnings}
                                onChange={(e) => setCppEarnings(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>C$10k</span>
                                <span>C$150k</span>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Years of CPP contributions
                                <span className={styles.inputValue}>{cppYears} yrs</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={1}
                                max={39}
                                value={cppYears}
                                onChange={(e) => setCppYears(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>1</span>
                                <span>39</span>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Claiming age
                                <span className={styles.inputValue}>{cppClaiming}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={60}
                                max={70}
                                value={cppClaiming}
                                onChange={(e) => setCppClaiming(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>60 (−36%)</span>
                                <span>70 (+42%)</span>
                            </div>
                        </div>

                        <div className={styles.estimate}>
                            <span className={styles.estimateLabel}>Estimated monthly benefit</span>
                            <span className={styles.estimateAmount}>C${cppMonthly.toLocaleString()}</span>
                            <span className={styles.estimateSubtext}>
                                C${(cppMonthly * 12).toLocaleString()}/year · starts age {cppClaiming}
                            </span>
                        </div>

                        <button className={styles.addButton} onClick={saveCPP} disabled={cppSaving || cppMonthly === 0}>
                            {cppSaving ? 'Saving…' : 'Add to Projection'}
                        </button>
                    </div>
                )}

                {/* ── OAS ── */}
                {hasCAN && (
                    <div className={styles.pensionCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <span className={styles.cardFlag}>🇨🇦</span>
                                <h2 className={styles.cardTitle}>Old Age Security (OAS)</h2>
                            </div>
                            <span className={styles.cardBadge}>CAN</span>
                        </div>
                        <p className={styles.cardDesc}>
                            Flat benefit based solely on years of Canadian residency — no earnings history needed.
                            Maximum C$713/month at 65 requires 40 years. Prorated for fewer years.
                        </p>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Years living in Canada
                                <span className={styles.inputValue}>{oasYears} yrs</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={1}
                                max={40}
                                value={oasYears}
                                onChange={(e) => setOasYears(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>1</span>
                                <span>40 (full)</span>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Claiming age
                                <span className={styles.inputValue}>{oasClaiming}</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={65}
                                max={70}
                                value={oasClaiming}
                                onChange={(e) => setOasClaiming(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>65</span>
                                <span>70 (+36%)</span>
                            </div>
                        </div>

                        <div className={styles.estimate}>
                            <span className={styles.estimateLabel}>Estimated monthly benefit</span>
                            <span className={styles.estimateAmount}>C${oasMonthly.toLocaleString()}</span>
                            <span className={styles.estimateSubtext}>
                                C${(oasMonthly * 12).toLocaleString()}/year · starts age {oasClaiming}
                            </span>
                        </div>

                        <button className={styles.addButton} onClick={saveOAS} disabled={oasSaving || oasMonthly === 0}>
                            {oasSaving ? 'Saving…' : 'Add to Projection'}
                        </button>
                    </div>
                )}

                {/* ── NPS Annuity ── */}
                {npsAsset && (
                    <div className={styles.pensionCard}>
                        <div className={styles.cardHeader}>
                            <div>
                                <span className={styles.cardFlag}>🇮🇳</span>
                                <h2 className={styles.cardTitle}>NPS Annuity</h2>
                            </div>
                            <span className={styles.cardBadge}>India</span>
                        </div>
                        <p className={styles.cardDesc}>
                            At retirement, 40% of your NPS corpus must purchase an annuity. Estimated at a 6%/year
                            annuity rate. Your current NPS balance is projected to retirement.
                        </p>

                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Current NPS balance</span>
                            <span className={styles.infoValue}>{fmt(npsAsset.balance, 'INR')}</span>
                        </div>
                        <div className={styles.infoRow}>
                            <span className={styles.infoLabel}>Years to retirement</span>
                            <span className={styles.infoValue}>{yearsToRetirement} yrs</span>
                        </div>

                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>
                                Expected NPS return (%/yr)
                                <span className={styles.inputValue}>{npsReturn}%</span>
                            </label>
                            <input
                                type="range"
                                className={styles.slider}
                                min={6}
                                max={14}
                                step={0.5}
                                value={npsReturn}
                                onChange={(e) => setNpsReturn(Number(e.target.value))}
                            />
                            <div className={styles.sliderRange}>
                                <span>6%</span>
                                <span>14%</span>
                            </div>
                        </div>

                        <div className={styles.estimate}>
                            <span className={styles.estimateLabel}>Estimated monthly annuity</span>
                            <span className={styles.estimateAmount}>{fmt(npsMonthly, 'INR')}</span>
                            <span className={styles.estimateSubtext}>
                                {fmt(npsMonthly * 12, 'INR')}/year · starts age {retirementAge}
                                <br />
                                Based on projected corpus of{' '}
                                {fmt(npsAsset.balance * Math.pow(1 + npsReturn / 100, yearsToRetirement), 'INR')}
                            </span>
                        </div>

                        <button className={styles.addButton} onClick={saveNPS} disabled={npsSaving || npsMonthly === 0}>
                            {npsSaving ? 'Saving…' : 'Add to Projection'}
                        </button>
                    </div>
                )}

                {!hasUS && !hasCAN && !npsAsset && (
                    <div className={styles.emptyState}>
                        <p>No government pensions detected for your residency profile.</p>
                        <p className={styles.emptyHint}>
                            Add US or Canadian residency in the <strong>Residency</strong> page, or add an NPS account
                            in the <strong>Dashboard</strong> to unlock pension estimators.
                        </p>
                    </div>
                )}
            </div>

            <div className={styles.disclaimer}>
                <strong>Estimates only.</strong> Social Security uses the 2024 SSA bend-point formula; CPP uses the 2024
                YMPE; OAS uses the 2024 maximum. Actual benefits depend on your complete earnings record. Verify at
                ssa.gov (SS), canada.ca (CPP/OAS), and npscra.nsdl.co.in (NPS).
            </div>
        </div>
    );
}
